import { COSMOS_URL } from 'components/globals';
import type { CityLink } from 'components/marketplace/directory/CityLinkList';
import { DirectoryTemplate } from 'components/marketplace/directory/DirectoryTemplate';
import { EmptyStateNearby } from 'components/marketplace/directory/EmptyStateNearby';
import { getStateDirectoryPageData } from 'components/marketplace/directory/ssr';
import type { StateLink } from 'components/marketplace/directory/StateLinkList';
import type { ListingCardData } from 'components/marketplace/property/SimilarListings';
import type { CacheSafeGetServerSideProps } from 'declarations/shared/serverSideProps';
import type { NextPage } from 'next';

interface StateDirectoryPageProps {
  state: string;
  stateDisplayName: string;
  listings: ListingCardData[];
  totalCount: number;
  currentPage: number;
  nextPage?: number | null;
  citiesWithInventory: CityLink[];
  statesWithInventory: StateLink[];
  nearMeCity?: string | null;
  nearMeCityCount?: number | null;
  isEmpty?: boolean | null;
  nearestStates?: StateLink[];
  displayingCity?: string | null;
  availableCities?: Array<{ name: string; slug: string }> | null;
  marketSlug?: string | null;
}

const formatStateName = (slug: string): string => {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const StateDirectoryPage: NextPage<StateDirectoryPageProps> = (props) => {
  if (props.isEmpty && props.nearestStates) {
    return (
      <EmptyStateNearby
        stateName={props.stateDisplayName}
        nearestStates={props.nearestStates}
        allStates={props.statesWithInventory}
      />
    );
  }

  const seoTitle = `Homes for Sale in ${props.stateDisplayName} | Opendoor`;
  const seoDescription = `Browse homes for sale across ${props.stateDisplayName}. Find houses, condos, and town homes in cities throughout the state with Opendoor.`;
  const canonicalUrl = `${COSMOS_URL}/homes/state/${props.state}`;

  return (
    <DirectoryTemplate
      locationName={props.stateDisplayName}
      locationSlug={props.state}
      listings={props.listings}
      totalCount={props.totalCount}
      currentPage={props.currentPage}
      nextPage={props.nextPage}
      citiesWithInventory={props.citiesWithInventory}
      statesWithInventory={props.statesWithInventory}
      nearMeCity={props.nearMeCity}
      nearMeCityCount={props.nearMeCityCount}
      seoTitle={seoTitle}
      seoDescription={seoDescription}
      canonicalUrl={canonicalUrl}
      displayingCity={props.displayingCity}
      availableCities={props.availableCities}
      marketSlug={props.marketSlug ?? undefined}
    />
  );
};

export const getServerSideProps: CacheSafeGetServerSideProps<StateDirectoryPageProps> = async (
  context,
) => {
  const state = context.params?.state;

  if (typeof state !== 'string') {
    return { notFound: true };
  }

  const pageParam = context.query.page;
  const page = typeof pageParam === 'string' ? parseInt(pageParam, 10) : 1;
  const selectedCity = typeof context.query.city === 'string' ? context.query.city : undefined;
  const data = await getStateDirectoryPageData(state, page, context.req.headers, selectedCity);

  if (data.totalCount === 0 && page === 1) {
    const neighborHeader = context.req.headers['x-od-state-neighbors'];
    const neighborSlugs = (
      Array.isArray(neighborHeader) ? neighborHeader : (neighborHeader?.split(',') ?? [])
    ).filter(Boolean);
    const neighborSlugSet = new Set(neighborSlugs);
    const nearestStates = data.statesWithInventory
      .filter((candidate) => neighborSlugSet.has(candidate.slug))
      .slice(0, 3);
    return {
      props: {
        state,
        stateDisplayName: formatStateName(state),
        listings: [],
        totalCount: 0,
        currentPage: page,
        nextPage: null,
        citiesWithInventory: data.citiesWithInventory,
        statesWithInventory: data.statesWithInventory,
        isEmpty: true,
        nearestStates,
      },
    };
  }

  if (data.totalCount === 0 && page > 1) {
    return { notFound: true };
  }

  return {
    props: {
      state,
      stateDisplayName: formatStateName(state),
      listings: data.listings,
      totalCount: data.totalCount,
      currentPage: page,
      nextPage: data.nextPage ?? null,
      citiesWithInventory: data.citiesWithInventory,
      statesWithInventory: data.statesWithInventory,
      nearMeCity: data.nearMeCity ?? null,
      nearMeCityCount: data.nearMeCityCount ?? null,
      displayingCity: data.displayingCity ?? null,
      availableCities: data.availableCities ?? null,
      marketSlug: data.marketSlug ?? null,
    },
  };
};

export default StateDirectoryPage;
